"use client"

import { listMockUsers } from '@/actions/user-action';
import LoginForm from '@/components/auth/LoginForm';
import logger from '@/lib/edge-logger';
import useAppStore from '@/context/app-context';
import { useUserStore } from '@/context/user-context';
import { LoginCredential } from '@/types/frontend';
import { Form, message, Select, Spin } from 'antd';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { Suspense, useEffect, useState } from 'react';

export interface UserOption {
    label: string;
    value: string;
    password?: string;
}

const handleLoginRedirect = (data: any, router: any) => {
    const loginToPage: string = typeof data?.preferences?.loginToPage === 'string' ? data.preferences.loginToPage : '/';
    router.push(loginToPage);
};

const LoginPageContent: React.FC = () => {
    const searchParams = useSearchParams();
    const [loading, setLoading] = useState(false);

    const router = useRouter();
    const {  login, loginWithToken } = useUserStore();
    const { appInfo, loading: configLoading, loadAppConfig } = useAppStore();
    const [form] = Form.useForm();
    const [users, setUsers] = useState<UserOption[]>([]);

    useEffect(() => {
        const token = searchParams.get('token');
        if (token) {
            const handleTokenLogin = async () => {
                try {
                    setLoading(true);
                    const data = await loginWithToken(token);
                    handleLoginRedirect(data, router);
                } catch (error) {
                    logger.error('Token login failed:', error);
                    message.error('Token登录失败');
                } finally {
                    setLoading(false);
                }
            };
            handleTokenLogin();
        } else {
            const fetchData = async () => {
                try {
                    // 获取模拟用户列表
                    const userList = await listMockUsers();
                    setUsers(userList.map(user => ({
                        label: user.name,
                        value: user.account,
                        password: user.password
                    })));

                    // 获取应用配置信息
                    await loadAppConfig();
                } catch (error) {
                    logger.error('Failed to fetch data:', error);
                }
            };
            fetchData();
        }
    }, [searchParams, router]);

    const handleUserSelect = (selectedOption: UserOption) => {
        form.setFieldsValue({
            account: selectedOption.value,
            password: selectedOption.password || '123456'
        });
    };

    const handleLogin = async (values: LoginCredential) => {
        try {
            setLoading(true);
            const data = await login({
                account: values.account,
                password: values.password
            });
            handleLoginRedirect(data, router);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex h-screen bg-white-50">
            {/* 加载动画 - 使用旋转效果 */}
            {(loading || configLoading) && (
                <div className="fixed inset-0 bg-white z-50 flex flex-col items-center justify-center overflow-hidden">
                    <Spin size='large' />
                    <div className="mt-4">{configLoading ? "正在加载..." : "正在登录..."}</div>
                </div>
            )}
            {!configLoading && (
                <>
                    {/* 左侧企业形象区域 */}
                    <div className="hidden md:flex md:w-1/2 bg-blue-600 items-center justify-center p-12 relative overflow-hidden">
                        {/* 增强的背景动画效果 */}
                        <div className="absolute inset-0 animate-fadeIn">

                            {/* 背景图片 */}
                            {appInfo.loginBgImage && (
                                <Image
                                    src={appInfo.loginBgImage}
                                    alt="Ship and Bridge"
                                    fill
                                    priority
                                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                    style={{ objectFit: 'cover' }}
                                    className="opacity-20"
                                />
                            )}
                        </div>
                        <div className="max-w-md text-white flex flex-col items-center -mt-10 relative z-10">
                            {appInfo.loginLogoImage && (
                                <Image
                                    priority
                                    src={appInfo.loginLogoImage}
                                    alt={appInfo.title || 'System'}
                                    width={200}
                                    height={152}
                                    className="mb-8"
                                />
                            )}
                            <h2 className="text-3xl font-bold mb-4">{appInfo.slogan}</h2>
                            <p className="text-lg">
                                {appInfo.slogan_sub}
                            </p>
                        </div>
                    </div>

                    {/* 右侧登录表单区域 */}
                    <div className="w-full md:w-1/2 flex flex-col justify-center items-center p-8">
                        <div className="w-full max-w-md bg-white p-10 rounded-lg shadow-md">
                            <h1 className="text-2xl font-bold text-gray-800 mb-8 text-center">
                                欢迎使用 {appInfo.title_cn}
                                <div className="text-2xl font-bold mb-4 mt-4">{appInfo.tenant} {appInfo.title}</div>
                            </h1>
                            <div className="space-y-6 mb-6">
                                {users.length > 0 && (
                                    <div>
                                        <Select
                                            className="w-full"
                                            placeholder="选择模拟用户"
                                            onChange={(_value, option) => handleUserSelect(option as UserOption)}
                                            options={users}
                                        />
                                    </div>
                                )}
                            </div>
                            <LoginForm form={form} onFinish={handleLogin} />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default function LoginPage() {
    return (
        <Suspense fallback={null}>
            <LoginPageContent />
        </Suspense>
    );
}